import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import TeamBadge from "@/components/base/TeamBadge";
import { featuredEvents, sportNames, type SportKey } from "@/mocks/events";

const heroImages: Record<SportKey, string> = {
  basketball:
    "https://readdy.ai/api/search-image?query=Dark%20cinematic%20indoor%20basketball%20arena%20with%20dramatic%20warm%20orange%20spotlights%20and%20silhouetted%20cheering%20crowd%2C%20deep%20black%20shadows%2C%20atmospheric%20haze%2C%20professional%20sports%20broadcast%20mood%2C%20high%20contrast%2C%20no%20text&width=1920&height=1080&seq=jrs-hero-basketball&orientation=landscape",
  football:
    "https://readdy.ai/api/search-image?query=Dark%20cinematic%20football%20stadium%20at%20night%20under%20intense%20floodlights%20with%20deep%20green%20pitch%20glow%20and%20warm%20orange%20rim%20light%2C%20black%20atmospheric%20haze%2C%20silhouetted%20stands%2C%20professional%20sports%20broadcast%20mood%2C%20high%20contrast%2C%20no%20text&width=1920&height=1080&seq=jrs-hero-football&orientation=landscape",
  esports:
    "https://readdy.ai/api/search-image?query=Dark%20futuristic%20esports%20tournament%20stage%20with%20neon%20orange%20and%20red%20lighting%20beams%2C%20giant%20LED%20screens%20glowing%2C%20deep%20black%20atmosphere%2C%20dramatic%20haze%2C%20professional%20gaming%20arena%2C%20high%20contrast%2C%20no%20text&width=1920&height=1080&seq=jrs-hero-esports&orientation=landscape",
};

const slides = featuredEvents.slice(0, 5);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, []);

  const go = (next: number) => {
    setIndex((next + slides.length) % slides.length);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const current = slides[index];

  return (
    <section
      className="relative w-full overflow-hidden border-b border-background-200"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 h-full w-full transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={heroImages[slide.sport]}
            alt={`${sportNames[slide.sport]}赛事直播现场画面`}
            title={`${slide.title}直播`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40" />

      <div className="relative mx-auto flex min-h-[560px] w-full flex-col justify-end px-4 pb-10 pt-24 md:min-h-[640px] md:px-6 md:pb-16 lg:justify-center lg:pt-16">
        <div key={current.id} className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent-500/40 bg-accent-500/10 px-3 py-1 text-xs font-semibold text-accent-400">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
            {current.status === "live" ? "正在直播" : "即将开始"} · {current.league}
          </span>

          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-5xl">
            {current.title}
          </h1>

          <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {current.description}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4 rounded-lg border border-background-200 bg-background-100/80 p-4 backdrop-blur">
            <div className="flex items-center gap-3">
              <TeamBadge team={current.home} size={44} />
              <span className="font-heading text-sm font-semibold text-foreground-900">
                {current.home.name}
              </span>
            </div>
            <div className="font-heading text-xl font-bold text-primary-400">
              {current.status === "upcoming" ? "VS" : `${current.homeScore} : ${current.awayScore}`}
            </div>
            <div className="flex items-center gap-3">
              <span className="font-heading text-sm font-semibold text-foreground-900">
                {current.away.name}
              </span>
              <TeamBadge team={current.away} size={44} />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/live/${current.id}`}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-live-line text-base" />
              进入直播间
            </Link>
            <Link
              to={`/category/${current.sport}`}
              className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/50 hover:text-primary-400 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-list-check-2" />
              查看{sportNames[current.sport]}赛事
            </Link>
            <span className="flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-time-line" />
              {current.time} · {current.progress}
            </span>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(index - 1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 bg-background-100 text-foreground-700 transition-colors hover:border-primary-500/50 hover:text-primary-400 cursor-pointer"
              aria-label="上一个直播"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 bg-background-100 text-foreground-700 transition-colors hover:border-primary-500/50 hover:text-primary-400 cursor-pointer"
              aria-label="下一个直播"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => go(i)}
                aria-label={`切换到第 ${i + 1} 个直播`}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-8 bg-primary-500" : "w-3 bg-background-400"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}